import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { useEndpoints } from "../api/ApiContext";
import { useAuth } from "../auth/AuthProvider";
import { useStaffSession } from "../auth/guards";
import { homeFor } from "../auth/pages";
import { BrandMark, Icon, type IconName } from "../components/Icon";
import { Banner, Button } from "../components/ui";
import { EnvironmentIndicator } from "./EnvironmentIndicator";
import { ThemeToggle } from "./ThemeToggle";

// The staff console's shell: the same layout as the portals (sidebar,
// top bar, skip link, focus to <main> on navigation), its own navigation,
// and a queue count beside every area that has a list endpoint. Areas
// with no list endpoint (debt cases, write-offs, safety findings) open by
// id; web/API-GAPS.md "Staff console" names the missing lists.

/** Query keys shared by the shell's counts and the screens, so one fetch serves both. */
export const staffKeys = {
  reviewQueue: ["staff", "review-queue"] as const,
  disputeCount: ["staff", "dispute-count"] as const,
  disputes: (state: string) => ["staff", "disputes", state] as const,
  dispute: (id: string) => ["staff", "dispute", id] as const,
  providerOps: ["staff", "provider-operations"] as const,
  clientMatrix: ["staff", "client-matrix"] as const,
  quarantines: ["staff", "quarantine-count"] as const,
  debtCase: (id: string) => ["staff", "debt-case", id] as const,
  receivable: (kind: string, id: string) => ["staff", "receivable", kind, id] as const,
};

interface StaffNavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  count?: { value: number | null; more?: boolean };
}

function useQueueCounts() {
  const ep = useEndpoints();
  const review = useQuery({ queryKey: staffKeys.reviewQueue, queryFn: () => ep.staff.review.queue() });
  const disputes = useQuery({ queryKey: staffKeys.disputeCount, queryFn: () => ep.staff.disputes.list("open") });
  const ops = useQuery({ queryKey: staffKeys.providerOps, queryFn: () => ep.staff.providerOperations.unresolved() });
  const matrix = useQuery({ queryKey: staffKeys.clientMatrix, queryFn: () => ep.staff.clientMatrix.list() });
  const quarantines = useQuery({ queryKey: staffKeys.quarantines, queryFn: () => ep.staff.reconciliation.quarantines("open") });
  return {
    review: review.data ? review.data.items.length : null,
    disputes: disputes.data ? disputes.data.disputes.length : null,
    disputesMore: !!disputes.data?.next_cursor,
    ops: ops.data ? ops.data.operations.length : null,
    suspended: matrix.data ? matrix.data.entries.filter((e) => e.status !== "validated").length : null,
    quarantined: quarantines.data ? quarantines.data.quarantines.length : null,
  };
}

function NavCount({ value, more, noun }: { value: number | null; more?: boolean; noun: string }) {
  if (value === null) return null;
  return (
    <span className={`staff-nav-count${value > 0 ? " staff-nav-count-active" : ""}`}>
      {value}
      {more ? "+" : ""} <span className="visually-hidden">{noun}</span>
    </span>
  );
}

export function StaffShell() {
  const { me } = useStaffSession();
  const { logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const counts = useQueueCounts();

  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    const main = document.getElementById("main");
    if (main && document.activeElement !== main && !navOpen) main.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const groups: Array<{ label: string; items: Array<StaffNavItem & { noun?: string }> }> = [
    {
      label: "Queues",
      items: [
        { to: "/staff", label: "Overview", icon: "home", end: true },
        { to: "/staff/review", label: "Review queue", icon: "list", count: { value: counts.review }, noun: "waiting" },
        { to: "/staff/disputes", label: "Disputes", icon: "scale", count: { value: counts.disputes, more: counts.disputesMore }, noun: "open" },
        { to: "/staff/provider-operations", label: "Provider operations", icon: "banknote", count: { value: counts.ops }, noun: "unresolved" },
      ],
    },
    {
      label: "Money",
      items: [
        { to: "/staff/debt-cases", label: "Debt cases", icon: "receipt" },
        { to: "/staff/write-offs", label: "Write-offs", icon: "undo" },
        { to: "/staff/reconciliation", label: "Reconciliation", icon: "chart", count: { value: counts.quarantined }, noun: "quarantined" },
        { to: "/staff/promotions", label: "Promotions", icon: "wallet" },
      ],
    },
    {
      label: "Safety & serving",
      items: [
        { to: "/staff/safety", label: "Safety findings", icon: "shield" },
        { to: "/staff/breaker", label: "Circuit breaker", icon: "alert" },
        { to: "/staff/client-matrix", label: "Client matrix", icon: "code", count: { value: counts.suspended }, noun: "suspended" },
      ],
    },
    { label: "Account", items: [{ to: "/staff/security", label: "Sign-in & security", icon: "lock" }] },
    // A dual-role account (migration 0070 cmd/ops grant-reviewer granted
    // platform_reviewer to an existing advertiser/publisher party) has an
    // own-portal home to go back to; a pure platform-party account doesn't.
    ...(me.party_type !== "platform"
      ? [{ label: "Your account", items: [{ to: homeFor(me.party_type), label: `Back to ${me.party_type === "advertiser" ? "Advertiser" : "Publisher"} portal`, icon: "home" as const }] }]
      : []),
  ];

  return (
    <div className="shell staff-shell" data-nav-open={navOpen}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="shell-sidebar" aria-label="Staff navigation">
        <NavLink to="/staff" className="brand">
          <BrandMark />
          <span>
            VADS <span className="brand-portal">Staff</span>
          </span>
        </NavLink>
        <nav className="nav">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="nav-group-label" id={`staff-nav-${g.label}`}>
                {g.label}
              </div>
              <ul aria-labelledby={`staff-nav-${g.label}`}>
                {g.items.map((item) => (
                  <li key={item.to}>
                    <NavLink to={item.to} end={item.end} className="nav-link">
                      <Icon name={item.icon} />
                      <span>{item.label}</span>
                      {item.count ? <NavCount value={item.count.value} more={item.count.more} noun={item.noun ?? ""} /> : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="shell-sidebar-footer">
          <span className="muted xs break-all">Staff · {me.email}</span>
          <ThemeToggle />
          <Button variant="secondary" size="sm" onClick={() => void logout()}>
            <Icon name="logout" />
            Sign out
          </Button>
        </div>
      </aside>
      <div className="shell-main">
        <header className="shell-topbar">
          <button type="button" className="btn btn-ghost btn-sm menu-toggle" aria-expanded={navOpen} onClick={() => setNavOpen((o) => !o)}>
            <Icon name={navOpen ? "x" : "menu"} />
            Menu
          </button>
          <span className="small muted">Staff console · platform reviewer</span>
          <span className="row">
            <EnvironmentIndicator />
          </span>
        </header>
        <main id="main" className="shell-content staff-content" tabIndex={-1}>
          {!me.mfa.enrolled ? (
            <Banner tone="warning" title="Set up two-step verification">
              Several staff decisions need a fresh verification, which needs a factor. Set one up under Sign-in &amp; security.
            </Banner>
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
